import { useEffect, useRef } from 'react';
import { grayFromRgba } from '../../utils/spores/focus';
import { analyseGray } from '../../utils/camera/frameMetrics';
import { createFrameAnalyser, frameWorkerSupported } from '../../utils/camera/frameAnalyser';
import { makeLiveCounter } from '../../utils/spores/liveCount';

/*
 * The live camera's frame pipeline: five times a second (SAMPLE_MS) one small
 * frame (SAMPLE_WIDTH wide) is measured (utils/camera/frameMetrics.js) and handed
 * to `onSample`. The focus meter (useFocusMeter.js) and the capture gate
 * (useCaptureGate.js) both read from it.
 *
 * The frame goes to a worker where the browser can (frameAnalyser.js), else, or
 * once the worker fails, it is read here as the app always did (sampleVideo;
 * tests inject `sampler`, which always reads here).
 *
 * Nothing is read while the page is hidden. Where the browser tells when a new
 * frame is shown (requestVideoFrameCallback), a frame it has already read is
 * skipped: a slow camera would otherwise read as a perfectly still picture. If
 * none has come for STALE_FRAME_MS the camera may only be off screen, so it reads
 * anyway.
 *
 * With `spores`, each sample also carries the live count (utils/spores/liveCount.js),
 * read wherever the frame is. Turning it on or off doesn't restart the pipeline.
 */
export const SAMPLE_WIDTH = 480;
export const SAMPLE_MS = 200;
export const STALE_FRAME_MS = 1000;

/** The video's frame, SAMPLE_WIDTH wide, as grey levels; null before it has one or without a canvas. */
export function sampleVideo(video, canvas) {
  if (!video || !canvas || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const w = SAMPLE_WIDTH;
  const h = Math.max(1, Math.round((video.videoHeight * w) / video.videoWidth));
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext ? canvas.getContext('2d', { willReadFrequently: true }) : null;
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return { gray: grayFromRgba(ctx.getImageData(0, 0, w, h).data, w, h), w, h };
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Calls `tick` every intervalMs that has a frame worth reading. Returns the stop. */
function startTicks(videoRef, intervalMs, tick) {
  let watched = null;
  let handle = null;
  let fresh = true;
  let lastTick = -Infinity;

  const onFrame = () => {
    fresh = true;
    if (watched) handle = watched.requestVideoFrameCallback(onFrame);
  };
  const unwatch = () => {
    if (watched && handle !== null && watched.cancelVideoFrameCallback) watched.cancelVideoFrameCallback(handle);
    watched = null;
    handle = null;
  };
  const watch = (video) => {
    if (video === watched) return;
    unwatch();
    if (video && typeof video.requestVideoFrameCallback === 'function') {
      watched = video;
      handle = video.requestVideoFrameCallback(onFrame);
    }
  };

  const timer = setInterval(() => {
    if (pageHidden()) return;
    watch(videoRef.current);
    const t = now();
    if (watched && !fresh && t - lastTick < STALE_FRAME_MS) return;
    fresh = false;
    lastTick = t;
    tick();
  }, intervalMs);
  return () => {
    clearInterval(timer);
    unwatch();
  };
}

/** While `live`, hands `onSample` each frame's numbers: {sharpness, motion, mean, dark, bright, at}, with `spores`, spores too. */
export function useFrameSampler(videoRef, live, onSample, { sampler = sampleVideo, intervalMs = SAMPLE_MS, spores = false } = {}) {
  const latest = useRef(onSample);
  latest.current = onSample;
  const wantSpores = useRef(spores);
  wantSpores.current = spores;

  useEffect(() => {
    if (!live) return undefined;
    let canvas = null;
    let prevThumb = null;
    const countSpores = makeLiveCounter();
    const readHere = () => {
      if (!canvas && typeof document !== 'undefined') canvas = document.createElement('canvas');
      const startedAt = now();
      let frame = null;
      try {
        frame = sampler(videoRef.current, canvas);
      } catch {
        frame = null;
      }
      if (!frame) return;
      const { reading, thumb } = analyseGray(frame.gray, frame.w, frame.h, prevThumb);
      prevThumb = thumb;
      const counted = wantSpores.current ? { spores: countSpores(frame.gray, frame.w, frame.h, startedAt) } : {};
      latest.current({ ...reading, ...counted, at: now() });
    };

    let analyser = sampler === sampleVideo && frameWorkerSupported()
      ? createFrameAnalyser({
        onSample: sample => latest.current(sample),
        onFailed: () => { analyser = null; },
      })
      : null;
    const stop = startTicks(videoRef, intervalMs, () => {
      if (analyser) analyser.send(videoRef.current, SAMPLE_WIDTH, { spores: wantSpores.current });
      else readHere();
    });
    return () => {
      stop();
      if (analyser) analyser.close();
    };
  }, [live, videoRef, sampler, intervalMs]);
}
